"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, EyeOff, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const trustIndicators = [{
  text: "Continuous broker scanning"
}, {
  text: "Automated removal requests"
}];
const features = [{
  text: "Automated data removal requests"
}, {
  text: "Continuous broker monitoring"
}, {
  text: "Personal data exposure reports"
}];
export default function Incogni() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.2 : 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="incognipromo" data-nav="dark" className="relative w-full overflow-hidden bg-[hsl(221,40%,4%)] py-20 text-white lg:py-32">
      {/* Decorative background numeral */}
      <div className="pointer-events-none absolute -top-8 -left-4 select-none font-mono text-[200px] font-bold leading-none text-white/5 md:-top-12 md:left-4 md:text-[280px]" aria-hidden="true">
        03
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }}>
          {/* Left Column */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-5 w-1 rounded-full bg-primary" aria-hidden="true" />
              <span className="font-mono text-4xl font-semibold tracking-widest text-primary uppercase sm:text-5xl lg:text-6xl">
                03 // Incogni
              </span>
            </div>

            <h2 className="mt-4 font-default text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Erase your digital footprint
            </h2>

            <p className="mt-5 text-base leading-relaxed text-white/70 sm:text-lg">
              Data brokers harvest your private contact details, home address,
              and identity records for sale. Incogni sends recurring, automated
              legal removal demands to clear your information and keep it off the
              web.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              {trustIndicators.map((item, i) => <div key={item.text} data-index={i} className="inline-flex items-center gap-2 rounded-md border border-[hsl(221,30%,18%)] bg-[hsl(221,30%,8%)] px-3.5 py-2 text-xs font-medium text-white/80">
                  <ShieldCheck className="size-4 shrink-0 text-primary" />
                  <span>{item.text}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <Card className="relative overflow-hidden rounded-lg border border-[hsl(221,30%,15%)] bg-[hsl(221,30%,8%)] p-8 text-white shadow-2xl md:p-12">
              {/* L-shaped top-left bracket */}
              <div className="pointer-events-none absolute top-3 left-3 size-4 border-t-2 border-l-2 border-primary" aria-hidden="true" />
              {/* L-shaped bottom-right bracket */}
              <div className="pointer-events-none absolute right-3 bottom-3 size-4 border-r-2 border-b-2 border-primary" aria-hidden="true" />

              <CardContent className="flex flex-col gap-6 p-0">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex size-16 items-center justify-center rounded-lg bg-primary/10 text-primary md:size-20">
                    <EyeOff className="size-8 md:size-10" />
                  </div>
                  <Badge variant="outline" className="border-primary/20 bg-primary/10 px-3 py-1 font-mono text-xs text-primary uppercase">
                    Best for data privacy
                  </Badge>
                </div>

                <div>
                  <h3 className="font-default text-2xl font-bold text-white sm:text-3xl md:text-4xl">
                    Personal Data Removal
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/70 sm:text-base">
                    Continuously scans and purges your records from hundreds of
                    commercial data brokers, people-search websites, and marketing
                    databases.
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  {features.map((feature, i) => <div key={feature.text} data-index={i} className="flex items-center gap-3 text-sm text-white/90 sm:text-base">
                      <CheckCircle2 className="size-5 shrink-0 text-primary" />
                      <span>{feature.text}</span>
                    </div>)}
                </div>

                <div className="pt-4">
                  <motion.div whileHover={shouldReduceMotion ? undefined : {
                  scale: 1.02
                }} whileTap={shouldReduceMotion ? undefined : {
                  scale: 0.98
                }}>
                    <Button asChild size="lg" className="h-14 w-full rounded-md bg-primary px-8 text-base font-semibold text-primary-foreground shadow-lg transition-[background-color,box-shadow] hover:bg-primary/90">
                      <Link to="https://vpngenix.com/go/Incogni" newTab className="flex items-center justify-center gap-2">
                        <span>Visit Site</span>
                        <ArrowRight className="size-5 shrink-0" />
                      </Link>
                    </Button>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}